/**
 * Debug Sprite Tuner
 *
 * Visual tool for tuning trainer face configs, Pokemon sprite sizes, and profile icons.
 * Access at /debug-sprites (add route temporarily)
 */

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Copy, RotateCcw, ChevronRight } from 'lucide-react';
import { TRAINER_FACE_CONFIG, SUPPORT_TRAINER_IMAGES } from '../constants/trainerImages';
import { POKEAPI_NAME_OVERRIDES } from '../utils/gameUtils';
import { ICON_CONFIG } from '../components/ProfileIcon';
import { POKEMON_SPRITE_CONFIG } from '../constants/pokemonSpriteConfig';
import { fetchTrialData } from '../data/elite4Trials';
import { TYPE_COLORS, mapToGameType } from '../components/TypeIcon';

// Cache for fetched sprite URLs
const spriteUrlCache = {};

const TrainerTuner = () => {
  const trainerNames = Object.keys(SUPPORT_TRAINER_IMAGES);
  const [selectedTrainer, setSelectedTrainer] = useState(trainerNames[0]);
  const [configs, setConfigs] = useState({ ...TRAINER_FACE_CONFIG });

  const currentConfig = configs[selectedTrainer] || configs.default;

  const updateConfig = (field, value) => {
    setConfigs(prev => ({
      ...prev,
      [selectedTrainer]: {
        ...currentConfig,
        [field]: parseFloat(value)
      }
    }));
  };

  const copyConfig = () => {
    const output = Object.entries(configs)
      .filter(([name]) => name !== 'default')
      .map(([name, cfg]) => `  '${name}': { scale: ${cfg.scale}, offsetY: ${cfg.offsetY}, offsetX: ${cfg.offsetX} },`)
      .join('\n');
    navigator.clipboard.writeText(output);
    alert('Copied to clipboard!');
  };

  const trainerImage = SUPPORT_TRAINER_IMAGES[selectedTrainer];

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Trainer Face Tuner</h2>

      {/* Trainer selector */}
      <div className="mb-4">
        <select
          value={selectedTrainer}
          onChange={(e) => setSelectedTrainer(e.target.value)}
          className="w-full p-2 border rounded"
        >
          {trainerNames.map(name => (
            <option key={name} value={name}>{name}</option>
          ))}
        </select>
      </div>

      {/* Preview - matches the actual training tile size */}
      <div className="flex gap-4 mb-4">
        {/* Small preview (training tile size) */}
        <div className="text-center">
          <div className="text-xs mb-1">Training Tile (40x40)</div>
          <div
            className="w-10 h-10 rounded-full overflow-hidden border-2 border-yellow-400 bg-gray-100 mx-auto"
          >
            <img
              src={trainerImage}
              alt={selectedTrainer}
              className="w-full h-full object-cover"
              style={{
                transform: `scale(${currentConfig.scale}) translate(${currentConfig.offsetX}%, ${currentConfig.offsetY}%)`,
                transformOrigin: 'top center'
              }}
            />
          </div>
        </div>

        {/* Larger preview for easier tuning */}
        <div className="text-center">
          <div className="text-xs mb-1">Large Preview (120x120)</div>
          <div
            className="w-30 h-30 rounded-full overflow-hidden border-2 border-yellow-400 bg-gray-100 mx-auto"
            style={{ width: 120, height: 120 }}
          >
            <img
              src={trainerImage}
              alt={selectedTrainer}
              className="w-full h-full object-cover"
              style={{
                transform: `scale(${currentConfig.scale}) translate(${currentConfig.offsetX}%, ${currentConfig.offsetY}%)`,
                transformOrigin: 'top center'
              }}
            />
          </div>
        </div>

        {/* Original image for reference */}
        <div className="text-center">
          <div className="text-xs mb-1">Original</div>
          <img src={trainerImage} alt={selectedTrainer} className="h-32 border" />
        </div>
      </div>

      {/* Sliders */}
      <div className="space-y-4 mb-4">
        <div>
          <label className="block text-sm font-medium">
            Scale: {currentConfig.scale}
          </label>
          <input
            type="range"
            min="1"
            max="5"
            step="0.1"
            value={currentConfig.scale}
            onChange={(e) => updateConfig('scale', e.target.value)}
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium">
            Offset Y: {currentConfig.offsetY}%
          </label>
          <input
            type="range"
            min="-50"
            max="10"
            step="1"
            value={currentConfig.offsetY}
            onChange={(e) => updateConfig('offsetY', e.target.value)}
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium">
            Offset X: {currentConfig.offsetX}%
          </label>
          <input
            type="range"
            min="-20"
            max="20"
            step="1"
            value={currentConfig.offsetX}
            onChange={(e) => updateConfig('offsetX', e.target.value)}
            className="w-full"
          />
        </div>
      </div>

      {/* Current values */}
      <div className="bg-gray-100 p-2 rounded mb-4 font-mono text-sm">
        '{selectedTrainer}': {'{'} scale: {currentConfig.scale}, offsetY: {currentConfig.offsetY}, offsetX: {currentConfig.offsetX} {'}'},
      </div>

      {/* Copy all button */}
      <button
        onClick={copyConfig}
        className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
      >
        <Copy size={16} />
        Copy All Configs
      </button>

      {/* Grid of all trainers for quick overview */}
      <div className="mt-8">
        <h3 className="font-bold mb-2">All Trainers Preview</h3>
        <div className="grid grid-cols-8 gap-2">
          {trainerNames.map(name => {
            const cfg = configs[name] || configs.default;
            const img = SUPPORT_TRAINER_IMAGES[name];
            return (
              <div
                key={name}
                className={`text-center cursor-pointer p-1 rounded ${selectedTrainer === name ? 'bg-yellow-200' : 'hover:bg-gray-100'}`}
                onClick={() => setSelectedTrainer(name)}
              >
                <div
                  className="w-10 h-10 rounded-full overflow-hidden border border-gray-300 bg-gray-100 mx-auto"
                >
                  <img
                    src={img}
                    alt={name}
                    className="w-full h-full object-cover"
                    style={{
                      transform: `scale(${cfg.scale}) translate(${cfg.offsetX}%, ${cfg.offsetY}%)`,
                      transformOrigin: 'top center'
                    }}
                  />
                </div>
                <div className="text-[8px] truncate mt-1">{name}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const PokemonTuner = () => {
  // Get all Pokemon from the config, plus some common ones
  const configuredPokemon = Object.keys(POKEMON_SPRITE_CONFIG).filter(k => k !== 'default');
  const defaultPokemon = [
    'Pikachu', 'Eevee', 'Squirtle', 'Charmander', 'Bulbasaur',
    'Charizard', 'Blastoise', 'Venusaur', 'Mewtwo', 'Joltik',
    'Pidove', 'Pichu', 'Garchomp', 'Wailord', 'Electabuzz'
  ];

  // Combine and dedupe
  const samplePokemon = [...new Set([...configuredPokemon, ...defaultPokemon])].sort();

  const [customPokemon, setCustomPokemon] = useState('');
  const [extraPokemon, setExtraPokemon] = useState([]);
  const [selectedPokemon, setSelectedPokemon] = useState(samplePokemon[0] || 'Pikachu');

  // Initialize scale configs from POKEMON_SPRITE_CONFIG
  const [scaleConfigs, setScaleConfigs] = useState(() => {
    const initial = {};
    [...samplePokemon, ...extraPokemon].forEach(name => {
      initial[name] = POKEMON_SPRITE_CONFIG[name]?.scale || 1.0;
    });
    return initial;
  });

  const addPokemon = () => {
    if (customPokemon && !extraPokemon.includes(customPokemon) && !samplePokemon.includes(customPokemon)) {
      setExtraPokemon([...extraPokemon, customPokemon]);
      setScaleConfigs(prev => ({
        ...prev,
        [customPokemon]: POKEMON_SPRITE_CONFIG[customPokemon]?.scale || 1.0
      }));
      setSelectedPokemon(customPokemon);
      setCustomPokemon('');
    }
  };

  const updateScale = (name, scale) => {
    setScaleConfigs(prev => ({
      ...prev,
      [name]: parseFloat(scale)
    }));
  };

  const resetScale = (name) => {
    setScaleConfigs(prev => ({
      ...prev,
      [name]: POKEMON_SPRITE_CONFIG[name]?.scale || 1.0
    }));
  };

  const copyAllConfigs = () => {
    const allPokemon = [...samplePokemon, ...extraPokemon];
    const output = allPokemon
      .filter(name => scaleConfigs[name] !== 1.0)
      .sort()
      .map(name => `  '${name}': { scale: ${scaleConfigs[name]} },`)
      .join('\n');
    navigator.clipboard.writeText(output);
    alert('Copied to clipboard!');
  };

  const copySingleConfig = (name) => {
    const scale = scaleConfigs[name] || 1.0;
    const output = `  '${name}': { scale: ${scale} },`;
    navigator.clipboard.writeText(output);
  };

  const allPokemon = [...samplePokemon, ...extraPokemon];
  const currentScale = scaleConfigs[selectedPokemon] || 1.0;

  // Custom sprite preview that uses local scale instead of config
  const PreviewSprite = ({ name, size, scale }) => {
    const [spriteUrl, setSpriteUrl] = useState(spriteUrlCache[name] || null);
    const scaledSize = Math.round(size * scale);

    useEffect(() => {
      if (spriteUrlCache[name]) {
        setSpriteUrl(spriteUrlCache[name]);
        return;
      }

      const apiName = POKEAPI_NAME_OVERRIDES[name] || name.toLowerCase();
      fetch(`https://pokeapi.co/api/v2/pokemon/${apiName}`)
        .then(res => res.json())
        .then(data => {
          const bwAnimated = data.sprites?.versions?.['generation-v']?.['black-white']?.animated?.front_default;
          const url = bwAnimated || data.sprites.front_default;
          spriteUrlCache[name] = url;
          setSpriteUrl(url);
        })
        .catch(err => {
          console.error('Failed to fetch Pokemon:', name, err);
          setSpriteUrl('error');
        });
    }, [name]);

    if (!spriteUrl) {
      return (
        <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#999' }}>
          Loading...
        </div>
      );
    }

    if (spriteUrl === 'error') {
      return (
        <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#f00' }}>
          Error
        </div>
      );
    }

    return (
      <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        <img
          src={spriteUrl}
          alt={name}
          style={{
            width: scaledSize,
            height: scaledSize,
            objectFit: 'contain',
            imageRendering: 'pixelated'
          }}
        />
      </div>
    );
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Pokemon Sprite Scale Tuner</h2>

      {/* Add custom Pokemon */}
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          value={customPokemon}
          onChange={(e) => setCustomPokemon(e.target.value)}
          placeholder="Add Pokemon name (e.g., Pikachu)..."
          className="flex-1 p-2 border rounded"
          onKeyPress={(e) => e.key === 'Enter' && addPokemon()}
        />
        <button
          onClick={addPokemon}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
        >
          Add
        </button>
      </div>

      {/* Main editing area */}
      <div className="flex flex-wrap gap-8 mb-6">
        {/* Selected Pokemon preview at different sizes */}
        <div>
          <div className="text-sm font-medium mb-2">Preview: {selectedPokemon}</div>
          <div className="flex gap-4 items-end">
            <div className="text-center">
              <div className="text-xs mb-1">64px</div>
              <div className="border-2 border-blue-400 bg-gray-100 rounded" style={{ width: 64, height: 64 }}>
                <PreviewSprite name={selectedPokemon} size={64} scale={currentScale} />
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs mb-1">96px</div>
              <div className="border-2 border-blue-400 bg-gray-100 rounded" style={{ width: 96, height: 96 }}>
                <PreviewSprite name={selectedPokemon} size={96} scale={currentScale} />
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs mb-1">128px</div>
              <div className="border-2 border-blue-400 bg-gray-100 rounded" style={{ width: 128, height: 128 }}>
                <PreviewSprite name={selectedPokemon} size={128} scale={currentScale} />
              </div>
            </div>
          </div>
        </div>

        {/* Scale at 1.0 for comparison */}
        <div>
          <div className="text-sm font-medium mb-2">Original (scale 1.0)</div>
          <div className="flex gap-4 items-end">
            <div className="text-center">
              <div className="text-xs mb-1">64px</div>
              <div className="border border-gray-300 bg-gray-50 rounded" style={{ width: 64, height: 64 }}>
                <PreviewSprite name={selectedPokemon} size={64} scale={1.0} />
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs mb-1">96px</div>
              <div className="border border-gray-300 bg-gray-50 rounded" style={{ width: 96, height: 96 }}>
                <PreviewSprite name={selectedPokemon} size={96} scale={1.0} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scale slider */}
      <div className="max-w-lg mb-4">
        <label className="block text-sm font-medium mb-1">
          Scale: {currentScale.toFixed(2)}
          <span className="text-gray-400 ml-2">(1.0 = original, {'>'} 1 = larger, {'<'} 1 = smaller)</span>
        </label>
        <input
          type="range"
          min="0.5"
          max="2.5"
          step="0.05"
          value={currentScale}
          onChange={(e) => updateScale(selectedPokemon, e.target.value)}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>0.5 (smaller)</span>
          <span>1.0</span>
          <span>2.5 (larger)</span>
        </div>
      </div>

      {/* Current value */}
      <div className="bg-gray-100 p-2 rounded mb-4 font-mono text-sm flex items-center gap-2">
        <span>'{selectedPokemon}': {'{'} scale: {currentScale.toFixed(2)} {'}'},</span>
        <button
          onClick={() => copySingleConfig(selectedPokemon)}
          className="px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-xs"
        >
          Copy
        </button>
        <button
          onClick={() => resetScale(selectedPokemon)}
          className="px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-xs"
        >
          Reset
        </button>
      </div>

      {/* Copy all button */}
      <button
        onClick={copyAllConfigs}
        className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 mb-6"
      >
        <Copy size={16} />
        Copy All Non-Default Configs
      </button>

      {/* Grid of all Pokemon for quick overview */}
      <div className="mt-4">
        <h3 className="font-bold mb-2">All Pokemon (click to select)</h3>
        <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-2">
          {allPokemon.map(name => {
            const scale = scaleConfigs[name] || 1.0;
            const hasCustomScale = scale !== 1.0;
            return (
              <div
                key={name}
                className={`text-center cursor-pointer p-1 rounded ${
                  selectedPokemon === name
                    ? 'bg-blue-200 ring-2 ring-blue-500'
                    : hasCustomScale
                      ? 'bg-yellow-100 hover:bg-yellow-200'
                      : 'hover:bg-gray-100'
                }`}
                onClick={() => setSelectedPokemon(name)}
              >
                <div
                  className="border border-gray-300 bg-gray-50 rounded mx-auto flex items-center justify-center"
                  style={{ width: 48, height: 48 }}
                >
                  <PreviewSprite name={name} size={48} scale={scale} />
                </div>
                <div className="text-[8px] truncate mt-1">{name}</div>
                {hasCustomScale && (
                  <div className="text-[7px] text-blue-600">{scale.toFixed(1)}x</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 p-4 bg-blue-50 rounded">
        <p className="text-sm">
          <strong>Tip:</strong> Yellow background indicates Pokemon with custom scales.
          Adjust the scale to make smaller Pokemon appear larger and large Pokemon appear smaller,
          so they all look roughly the same size relative to each other.
        </p>
      </div>
    </div>
  );
};

// Trial Banner Tuner - for tuning Elite 4 sprites in trial banners
const TrialBannerTuner = () => {
  const [trialData, setTrialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState('kanto');
  const [selectedMember, setSelectedMember] = useState(0);
  const [configs, setConfigs] = useState({});

  // Load trial data from backend
  useEffect(() => {
    const loadData = async () => {
      const data = await fetchTrialData();
      if (data) {
        setTrialData(data);
        // Initialize configs from backend data
        const initialConfigs = {};
        Object.entries(data.regions).forEach(([regionKey, region]) => {
          region.members.forEach((member, idx) => {
            const key = `${regionKey}-${idx}`;
            initialConfigs[key] = member.spriteConfig || { scale: 2.5, offsetX: 0, offsetY: 0 };
          });
        });
        setConfigs(initialConfigs);
      }
      setLoading(false);
    };
    loadData();
  }, []);

  if (loading || !trialData) {
    return <div className="p-4">Loading trial data...</div>;
  }

  const regions = trialData.regions;
  const regionKeys = trialData.regionKeys;
  const currentRegion = regions[selectedRegion];
  const currentMember = currentRegion?.members[selectedMember];
  const configKey = `${selectedRegion}-${selectedMember}`;
  const currentConfig = configs[configKey] || { scale: 2.5, offsetX: 0, offsetY: 0 };

  const updateConfig = (field, value) => {
    setConfigs(prev => ({
      ...prev,
      [configKey]: {
        ...currentConfig,
        [field]: parseFloat(value)
      }
    }));
  };

  const copyConfig = () => {
    // Generate spriteConfig for all members in current region
    const output = currentRegion.members.map((member, idx) => {
      const cfg = configs[`${selectedRegion}-${idx}`] || { scale: 2.5, offsetX: 0, offsetY: 0 };
      return `      { name: '${member.name}', ..., spriteConfig: { scale: ${cfg.scale}, offsetX: ${cfg.offsetX}, offsetY: ${cfg.offsetY} }, ... },`;
    }).join('\n');
    navigator.clipboard.writeText(output);
    alert('Copied spriteConfig for ' + selectedRegion + ' region!');
  };

  const copyAllConfigs = () => {
    // Generate all spriteConfigs grouped by region
    const output = regionKeys.map(regionKey => {
      const region = regions[regionKey];
      const memberConfigs = region.members.map((member, idx) => {
        const cfg = configs[`${regionKey}-${idx}`] || { scale: 2.5, offsetX: 0, offsetY: 0 };
        return `spriteConfig: { scale: ${cfg.scale}, offsetX: ${cfg.offsetX}, offsetY: ${cfg.offsetY} }`;
      });
      return `// ${regionKey}\n${region.members.map((m, i) => `${m.name}: ${memberConfigs[i]}`).join('\n')}`;
    }).join('\n\n');
    navigator.clipboard.writeText(output);
    alert('Copied all spriteConfigs!');
  };

  // Render the slashed banner preview
  const renderBannerPreview = () => (
    <div
      className="relative w-full h-28 rounded-xl overflow-hidden shadow-lg"
      style={{
        background: `linear-gradient(135deg, ${currentRegion.color}, ${currentRegion.color}CC)`
      }}
    >
      {/* Left side text */}
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-left z-20">
        <h3 className="text-2xl font-bold text-white tracking-wide drop-shadow-lg">{currentRegion.name}</h3>
        <p className="text-white/80 text-sm font-medium">{currentRegion.region}</p>
      </div>

      {/* Slashed trainer sprites - spanning from left text area to right edge */}
      <div className="absolute right-0 top-0 h-full flex items-center" style={{ left: '100px' }}>
        {currentRegion.members.map((member, idx) => {
          const cfg = configs[`${selectedRegion}-${idx}`] || { scale: 2.5, offsetX: 0, offsetY: 0 };
          const isSelected = idx === selectedMember;
          return (
            <div
              key={idx}
              className={`relative h-full overflow-hidden cursor-pointer ${isSelected ? 'ring-2 ring-yellow-400' : ''}`}
              style={{
                flex: '1 1 0',
                minWidth: '60px',
                marginLeft: idx === 0 ? 0 : '-20px',
                zIndex: isSelected ? 10 : idx,
                clipPath: 'polygon(30% 0%, 100% 0%, 70% 100%, 0% 100%)',
                transform: 'skewX(-12deg)'
              }}
              onClick={() => setSelectedMember(idx)}
            >
              <div
                className="absolute inset-0 border-l-2 border-white/40"
                style={{
                  backgroundColor: TYPE_COLORS[mapToGameType(member.type)] || '#6B7280'
                }}
              >
                {member.sprite && (
                  <img
                    src={member.sprite}
                    alt={member.name}
                    className="absolute"
                    style={{
                      transform: `skewX(12deg) scale(${cfg.scale})`,
                      top: `calc(50% + ${cfg.offsetY}px)`,
                      left: `calc(50% + ${cfg.offsetX}px)`,
                      transformOrigin: 'center center',
                      width: '100%',
                      height: 'auto'
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Arrow indicator - overlaid on right edge */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/30 rounded-full p-2 z-10 backdrop-blur-sm">
        <ChevronRight size={18} className="text-white" />
      </div>
    </div>
  );

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Trial Banner Sprite Tuner</h2>

      {/* Region selector */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Region</label>
        <div className="flex gap-2 flex-wrap">
          {regionKeys.map(key => (
            <button
              key={key}
              onClick={() => { setSelectedRegion(key); setSelectedMember(0); }}
              className={`px-4 py-2 rounded capitalize ${
                selectedRegion === key ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              {key}
            </button>
          ))}
        </div>
      </div>

      {/* Banner Preview */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">Banner Preview (click a sprite to select)</label>
        {renderBannerPreview()}
      </div>

      {/* Member selector */}
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Elite 4 Member</label>
        <div className="flex gap-2">
          {currentRegion.members.map((member, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedMember(idx)}
              className={`px-4 py-2 rounded ${
                selectedMember === idx ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              {member.name}
            </button>
          ))}
        </div>
      </div>

      {/* Current member info */}
      <div className="mb-4 p-3 bg-gray-100 rounded flex items-center gap-4">
        <div
          className="w-16 h-16 rounded-full overflow-hidden border-2"
          style={{ borderColor: TYPE_COLORS[mapToGameType(currentMember?.type)] || '#6B7280' }}
        >
          <img
            src={currentMember?.sprite}
            alt={currentMember?.name}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div>
          <div className="font-bold">{currentMember?.name}</div>
          <div className="text-sm text-gray-600">{currentMember?.title}</div>
          <div className="text-xs text-gray-500">Type: {currentMember?.type}</div>
        </div>
      </div>

      {/* Larger single sprite preview */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">Isolated Sprite Preview</label>
        <div className="flex gap-4">
          {/* Slashed preview - matches banner exactly */}
          <div className="text-center">
            <div className="text-xs mb-1">Slashed (actual)</div>
            <div
              className="relative overflow-hidden"
              style={{
                width: '100px',
                height: '112px',
                clipPath: 'polygon(30% 0%, 100% 0%, 70% 100%, 0% 100%)',
                transform: 'skewX(-12deg)',
                backgroundColor: TYPE_COLORS[mapToGameType(currentMember?.type)] || '#6B7280'
              }}
            >
              {currentMember?.sprite && (
                <img
                  src={currentMember.sprite}
                  alt={currentMember.name}
                  className="absolute"
                  style={{
                    transform: `skewX(12deg) scale(${currentConfig.scale})`,
                    top: `calc(50% + ${currentConfig.offsetY}px)`,
                    left: `calc(50% + ${currentConfig.offsetX}px)`,
                    transformOrigin: 'center center',
                    width: '100%',
                    height: 'auto'
                  }}
                />
              )}
            </div>
          </div>

          {/* Original for reference */}
          <div className="text-center">
            <div className="text-xs mb-1">Original</div>
            <img
              src={currentMember?.sprite}
              alt={currentMember?.name}
              className="h-28 border rounded"
            />
          </div>
        </div>
      </div>

      {/* Sliders */}
      <div className="space-y-4 mb-4 max-w-lg">
        <div>
          <label className="block text-sm font-medium">
            Scale: {currentConfig.scale} <span className="text-gray-400">(default: 2.5)</span>
          </label>
          <input
            type="range"
            min="1"
            max="5"
            step="0.1"
            value={currentConfig.scale}
            onChange={(e) => updateConfig('scale', e.target.value)}
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium">
            Offset X: {currentConfig.offsetX}px <span className="text-gray-400">(+right, -left)</span>
          </label>
          <input
            type="range"
            min="-100"
            max="100"
            step="1"
            value={currentConfig.offsetX}
            onChange={(e) => updateConfig('offsetX', e.target.value)}
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium">
            Offset Y: {currentConfig.offsetY}px <span className="text-gray-400">(+down, -up)</span>
          </label>
          <input
            type="range"
            min="-100"
            max="100"
            step="1"
            value={currentConfig.offsetY}
            onChange={(e) => updateConfig('offsetY', e.target.value)}
            className="w-full"
          />
        </div>
      </div>

      {/* Current value */}
      <div className="bg-gray-100 p-2 rounded mb-4 font-mono text-sm overflow-x-auto">
        spriteConfig: {'{'} scale: {currentConfig.scale}, offsetX: {currentConfig.offsetX}, offsetY: {currentConfig.offsetY} {'}'}
      </div>

      {/* Buttons */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={copyConfig}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          <Copy size={16} />
          Copy {selectedRegion} Region
        </button>
        <button
          onClick={copyAllConfigs}
          className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
        >
          <Copy size={16} />
          Copy All Regions
        </button>
      </div>

      {/* All regions preview */}
      <div className="mt-8 space-y-4">
        <h3 className="font-bold">All Regions Preview</h3>
        {regionKeys.map(regionKey => {
          const region = regions[regionKey];
          return (
            <div
              key={regionKey}
              className={`cursor-pointer ${selectedRegion === regionKey ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => { setSelectedRegion(regionKey); setSelectedMember(0); }}
            >
              <div
                className="relative w-full h-28 rounded-xl overflow-hidden shadow-lg"
                style={{
                  background: `linear-gradient(135deg, ${region.color}, ${region.color}CC)`
                }}
              >
                {/* Left side text */}
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-left z-20">
                  <h3 className="text-2xl font-bold text-white tracking-wide drop-shadow-lg">{region.name}</h3>
                  <p className="text-white/80 text-sm font-medium">{region.region}</p>
                </div>

                {/* Slashed trainer sprites */}
                <div className="absolute right-0 top-0 h-full flex items-center" style={{ left: '100px' }}>
                  {region.members.map((member, idx) => {
                    const cfg = configs[`${regionKey}-${idx}`] || { scale: 2.5, offsetX: 0, offsetY: 0 };
                    return (
                      <div
                        key={idx}
                        className="relative h-full overflow-hidden"
                        style={{
                          flex: '1 1 0',
                          minWidth: '60px',
                          marginLeft: idx === 0 ? 0 : '-20px',
                          zIndex: idx,
                          clipPath: 'polygon(30% 0%, 100% 0%, 70% 100%, 0% 100%)',
                          transform: 'skewX(-12deg)'
                        }}
                      >
                        <div
                          className="absolute inset-0 border-l-2 border-white/40"
                          style={{
                            backgroundColor: TYPE_COLORS[mapToGameType(member.type)] || '#6B7280'
                          }}
                        >
                          {member.sprite && (
                            <img
                              src={member.sprite}
                              alt={member.name}
                              className="absolute"
                              style={{
                                transform: `skewX(12deg) scale(${cfg.scale})`,
                                top: `calc(50% + ${cfg.offsetY}px)`,
                                left: `calc(50% + ${cfg.offsetX}px)`,
                                transformOrigin: 'center center',
                                width: '100%',
                                height: 'auto'
                              }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Arrow indicator */}
                <div className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/30 rounded-full p-2 z-10 backdrop-blur-sm">
                  <ChevronRight size={18} className="text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 p-4 bg-blue-50 rounded">
        <p className="text-sm">
          <strong>Tip:</strong> After tuning, copy the configs and paste them into the backend
          <code className="mx-1 px-1 bg-blue-100 rounded">routes/trials.js</code> file.
          Then commit and push to deploy the changes.
        </p>
      </div>
    </div>
  );
};

// All available profile icons
const ALL_PROFILE_ICONS = [
  'pikachu', 'squirtle', 'charmander', 'bulbasaur',
  'mewtwo', 'gastly', 'officer-jenny', 'nurse-joy', 'delia-ketchum'
];

const ProfileIconTuner = () => {
  const iconNames = ALL_PROFILE_ICONS;
  const [selectedIcon, setSelectedIcon] = useState(iconNames[0]);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  // Initialize configs from ICON_CONFIG
  const [configs, setConfigs] = useState(() => {
    const initial = {};
    iconNames.forEach(icon => {
      const existing = ICON_CONFIG[icon] || {};
      initial[icon] = {
        scale: existing.scale || 1,
        offsetY: existing.offsetY || 0,
        offsetX: existing.offsetX || 0
      };
    });
    return initial;
  });

  const currentConfig = configs[selectedIcon] || { scale: 1, offsetY: 0, offsetX: 0 };
  const iconData = ICON_CONFIG[selectedIcon] || {};
  const imagePath = iconData.path || `/images/profile-icons/${selectedIcon}.png`;

  const updateConfig = (field, value) => {
    setConfigs(prev => ({
      ...prev,
      [selectedIcon]: {
        ...currentConfig,
        [field]: parseFloat(value)
      }
    }));
  };

  const resetConfig = () => {
    const existing = ICON_CONFIG[selectedIcon] || {};
    setConfigs(prev => ({
      ...prev,
      [selectedIcon]: {
        scale: existing.scale || 1,
        offsetY: existing.offsetY || 0,
        offsetX: existing.offsetX || 0
      }
    }));
  };

  const copyConfig = () => {
    const output = Object.entries(configs)
      .map(([name, cfg]) => {
        const iconInfo = ICON_CONFIG[name] || {};
        const displayName = iconInfo.name || name;
        const unlock = iconInfo.unlock ? `, unlock: '${iconInfo.unlock}', unlockDesc: '${iconInfo.unlockDesc}'` : '';
        return `  '${name}': { name: '${displayName}', path: '/images/profile-icons/${name}.png', scale: ${cfg.scale}, offsetY: ${cfg.offsetY}, offsetX: ${cfg.offsetX}${unlock} },`;
      })
      .join('\n');
    navigator.clipboard.writeText(output);
    alert('Copied to clipboard!');
  };

  // Handle image load to get dimensions
  const handleImageLoad = (e) => {
    setImageSize({ width: e.target.naturalWidth, height: e.target.naturalHeight });
  };

  // Render the final result preview at a given size (this is how it will actually look)
  const renderResultPreview = (size, label) => (
    <div className="text-center">
      <div className="text-xs mb-1">{label}</div>
      <div
        className="rounded-full overflow-hidden border-2 border-blue-400 bg-gray-100 mx-auto relative"
        style={{ width: size, height: size }}
      >
        <img
          src={imagePath}
          alt={selectedIcon}
          className="absolute top-0 left-0"
          style={{
            width: '100%',
            height: 'auto',
            transform: `scale(${currentConfig.scale}) translate(${currentConfig.offsetX}%, ${currentConfig.offsetY}%)`,
            transformOrigin: 'top center'
          }}
        />
      </div>
    </div>
  );

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Profile Icon Tuner</h2>

      {/* Icon selector */}
      <div className="mb-4">
        <select
          value={selectedIcon}
          onChange={(e) => setSelectedIcon(e.target.value)}
          className="w-full p-2 border rounded"
        >
          {iconNames.map(name => (
            <option key={name} value={name}>
              {ICON_CONFIG[name]?.name || name}
            </option>
          ))}
        </select>
      </div>

      {/* Main editing area */}
      <div className="flex flex-wrap gap-8 mb-6">
        {/* Full original image - scrollable to see entire image */}
        <div className="text-center">
          <div className="text-sm font-medium mb-2">Original Image (scroll to view full)</div>
          <div
            className="border-2 border-gray-300 rounded bg-gray-50 p-2 overflow-auto"
            style={{ maxHeight: 400, maxWidth: 300 }}
          >
            <img
              src={imagePath}
              alt={selectedIcon}
              onLoad={handleImageLoad}
              style={{ maxWidth: 'none', width: 'auto', height: 'auto' }}
            />
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {imageSize.width}x{imageSize.height}px (scroll if needed)
          </div>
        </div>

        {/* Large preview showing exactly what will be cropped */}
        <div className="text-center">
          <div className="text-sm font-medium mb-2">Circle Crop Preview (120px)</div>
          <div
            className="rounded-full overflow-hidden border-4 border-blue-500 bg-gray-100 mx-auto relative"
            style={{ width: 120, height: 120 }}
          >
            {/*
              Key fix: Don't use object-cover which pre-crops the image.
              Instead, set width:100% so image fills horizontally,
              then use transforms to position vertically.
              This allows accessing the FULL image height via translate.
            */}
            <img
              src={imagePath}
              alt={selectedIcon}
              className="absolute top-0 left-0"
              style={{
                width: '100%',
                height: 'auto',
                transform: `scale(${currentConfig.scale}) translate(${currentConfig.offsetX}%, ${currentConfig.offsetY}%)`,
                transformOrigin: 'top center'
              }}
            />
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Adjust sliders to position the face
          </div>
        </div>

        {/* Result previews at various sizes */}
        <div>
          <div className="text-sm font-medium mb-2">Size Previews</div>
          <div className="flex flex-wrap gap-3 items-end">
            {renderResultPreview(32, '32px')}
            {renderResultPreview(48, '48px')}
            {renderResultPreview(64, '64px')}
          </div>
        </div>
      </div>

      {/* Sliders */}
      <div className="space-y-4 mb-4 max-w-lg">
        <div>
          <label className="block text-sm font-medium">
            Scale (zoom): {currentConfig.scale} <span className="text-gray-400">(higher = more zoomed in)</span>
          </label>
          <input
            type="range"
            min="0.5"
            max="5"
            step="0.1"
            value={currentConfig.scale}
            onChange={(e) => updateConfig('scale', e.target.value)}
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium">
            Offset Y: {currentConfig.offsetY}% <span className="text-gray-400">(negative = move up to show face)</span>
          </label>
          <input
            type="range"
            min="-50"
            max="50"
            step="1"
            value={currentConfig.offsetY}
            onChange={(e) => updateConfig('offsetY', e.target.value)}
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium">
            Offset X: {currentConfig.offsetX}px <span className="text-gray-400">(negative = move left)</span>
          </label>
          <input
            type="range"
            min="-100"
            max="100"
            step="1"
            value={currentConfig.offsetX}
            onChange={(e) => updateConfig('offsetX', e.target.value)}
            className="w-full"
          />
        </div>
      </div>

      {/* Current values */}
      <div className="bg-gray-100 p-2 rounded mb-4 font-mono text-sm overflow-x-auto">
        '{selectedIcon}': {'{'} name: '{ICON_CONFIG[selectedIcon]?.name || selectedIcon}', path: '...', scale: {currentConfig.scale}, offsetY: {currentConfig.offsetY}, offsetX: {currentConfig.offsetX} {'}'},
      </div>

      {/* Buttons */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={resetConfig}
          className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
        >
          <RotateCcw size={16} />
          Reset
        </button>
        <button
          onClick={copyConfig}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          <Copy size={16} />
          Copy All Configs
        </button>
      </div>

      {/* Grid of all icons for quick overview */}
      <div className="mt-8">
        <h3 className="font-bold mb-2">All Icons Preview (click to select)</h3>
        <div className="grid grid-cols-9 gap-2">
          {iconNames.map(name => {
            const cfg = configs[name];
            const iconInfo = ICON_CONFIG[name] || {};
            const path = iconInfo.path || `/images/profile-icons/${name}.png`;
            return (
              <div
                key={name}
                className={`text-center cursor-pointer p-1 rounded ${selectedIcon === name ? 'bg-blue-200 ring-2 ring-blue-500' : 'hover:bg-gray-100'}`}
                onClick={() => setSelectedIcon(name)}
              >
                <div
                  className="w-12 h-12 rounded-full overflow-hidden border-2 border-gray-300 bg-gray-100 mx-auto relative"
                >
                  <img
                    src={path}
                    alt={name}
                    className="absolute top-0 left-0"
                    style={{
                      width: '100%',
                      height: 'auto',
                      transform: `scale(${cfg.scale}) translate(${cfg.offsetX}%, ${cfg.offsetY}%)`,
                      transformOrigin: 'top center'
                    }}
                  />
                </div>
                <div className="text-[9px] truncate mt-1">{iconInfo.name || name}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const DebugSpriteTuner = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('trainers');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b p-4 flex items-center gap-4 flex-wrap">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-lg font-bold">Sprite Tuner</h1>

        {/* Tabs */}
        <div className="flex gap-2 ml-4 flex-wrap">
          <button
            onClick={() => setActiveTab('trainers')}
            className={`px-4 py-2 rounded ${activeTab === 'trainers' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            Trainers
          </button>
          <button
            onClick={() => setActiveTab('pokemon')}
            className={`px-4 py-2 rounded ${activeTab === 'pokemon' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            Pokemon
          </button>
          <button
            onClick={() => setActiveTab('icons')}
            className={`px-4 py-2 rounded ${activeTab === 'icons' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            Profile Icons
          </button>
          <button
            onClick={() => setActiveTab('trials')}
            className={`px-4 py-2 rounded ${activeTab === 'trials' ? 'bg-red-500 text-white' : 'bg-gray-200'}`}
          >
            Trial Banners
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto">
        {activeTab === 'trainers' && <TrainerTuner />}
        {activeTab === 'pokemon' && <PokemonTuner />}
        {activeTab === 'icons' && <ProfileIconTuner />}
        {activeTab === 'trials' && <TrialBannerTuner />}
      </div>
    </div>
  );
};

export default DebugSpriteTuner;
